/**
 * Shared getNextStepInfo utility for order list pages.
 *
 * Single source of truth — replaces local definitions in
 * DepartmentOrders.js, MyOrders.js.
 *
 * @param {object} order        – order row (must include status, approval_info, delivery_info, etc.)
 * @param {object} [opts]       – optional config
 * @param {boolean} [opts.canAction]  – whether current user can act on this order
 * @param {string}  [opts.role]       – 'user' | 'admin' | 'buyer' | 'sub_department_lead'
 * @param {number|string} [opts.userId] – the viewer's user id. Omitted → read from
 *        `localStorage.sfx_user_id`. There is NO `opts.userName` any more; see the
 *        "Your Approval Needed" block below for why it was removed.
 * @returns {{ label: string, sublabel: string|null, action: string|null, color: string, icon: string }}
 */

import { APPROVAL_ROLE_LABELS, approverRoleLabel } from '../constants/statuses';

/**
 * Normalise a user id that may have arrived as a JSON number (from the API) or as
 * a string (from `localStorage.sfx_user_id`, which stores everything as text).
 *
 * Returns a positive integer, or null for anything that is not one. `parseInt` is
 * deliberately NOT used: it is not a validator, and `parseInt('12abc')` is 12 —
 * i.e. it would silently answer "yes, you are user 12". Absent / empty / malformed
 * all collapse to null, and null never equals null in the caller below, so an
 * unusable id can only ever mean "not proven to be the same person".
 *
 * @param {number|string|null|undefined} value
 * @returns {number|null}
 */
function normaliseUserId(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * The viewer's own user id, as the pages store it.
 *
 * ⚠️ TRANSITIONAL — THIS IS OWED WORK, NOT THE PATTERN TO COPY.
 *
 * `getNextStepInfo` needs the viewer's identity to decide "Your Approval Needed"
 * and no caller passes one: `DepartmentOrders.js:614` and `:898` supply
 * `{ canAction, role }`, and `MyOrders.js:703` supplies nothing at all (its
 * `role` defaults to `'user'`, which never reaches that branch). **Those two
 * `DepartmentOrders.js` call sites should pass `userId: currentUserId`
 * explicitly** — the value is already in scope there, read from this same key at
 * `DepartmentOrders.js:108` for `canPokeOrder` — at which point this fallback
 * becomes dead and should be deleted along with the `opts.userId` default.
 * It was not done in the change that introduced this (2026-08-06) only because
 * `DepartmentOrders.js` was owned by another writer at the time, and two writers
 * in one file has corrupted a worktree here before.
 *
 * ── 🔻 RELOCATED 2026-09-21 FROM `client/src/utils/CLAUDE.md`, VERBATIM ─────
 * Its condensed twin of the paragraphs above, moved here by owner ruling to
 * free headroom in that auto-loaded, byte-budgeted document. BYTE-IDENTICAL —
 * not rewritten, not trimmed, kept on one line so it can be reconstructed and
 * compared. 🚨 **READ ITS LAST SENTENCE AGAINST THE LINE NUMBERS ABOVE**: the
 * paragraph above cites `DepartmentOrders.js:614`, `:898`, `:108` and
 * `MyOrders.js:703`, and this records that those very numbers were re-cited to
 * `:983`/`:1294`/`:202` on 2026-08-08 and were wrong again within three days.
 * They are STALE here too and are left as-is deliberately — a citation known to
 * be wrong when written is inherited debt, never silently re-derived. Grep the
 * call shape, never the line.
 *
 * ⚠️ **`opts.userId` is TRANSITIONAL-optional**: no caller passes it yet, so the util falls back to reading `localStorage.sfx_user_id` itself. `DepartmentOrders.js`'s two `getNextStepInfo(order, { canAction: …, role: currentRole, userName: currentUser })` call sites should pass `userId: currentUserId` (already in scope beside `localStorage.getItem('sfx_user_id')`), after which the fallback is dead code and should go. ⚠️ Both still pass `userName: currentUser`, which the util **ignores** — harmless, but do not read it as evidence that `opts.userName` is live. 🔢 **Named by call shape, not by line, because this parenthesis has gone stale TWICE** — `:614`/`:898`/`:108`, re-cited to `:983`/`:1294`/`:202` on 2026-08-08, wrong again within three days. **Grep the call.**
 *
 * A hidden global read inside an otherwise pure utility is a real smell. It is
 * accepted here because the alternatives were a regression (an opts-only change
 * makes the label vanish for everyone, since nobody passes the id) or a second
 * writer in someone else's file. It must not calcify: `opts.userId` ALWAYS wins
 * when supplied, so this is a bridge for callers not yet updated, never a
 * preference.
 *
 * It introduces no new key and no new source of truth — `DepartmentOrders.js`,
 * `MyOrders.js` and `Approvals.js` all already read `sfx_user_id` for
 * `canPokeOrder`. It only stops the id being dropped on the floor between the
 * page and this function.
 *
 * Wrapped because `localStorage` throws in some privacy modes and inside a
 * sandboxed iframe; a mis-labelled row is not worth an exception escaping a
 * render. A failure yields null, and null is "not proven to be the same person".
 *
 * @returns {string|null}
 */
function readStoredViewerUserId() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem('sfx_user_id');
  } catch {
    return null;
  }
}

/**
 * Should the "Poke" control be offered for this order to this viewer?
 *
 * Single source of truth — replaces the copy of this predicate that lived in
 * MyOrders.js, DepartmentOrders.js and Approvals.js (five render sites between
 * them; three copies of the expression). Three copies of a predicate is how a
 * "fixed" bug stays live on the pages nobody remembered to edit.
 *
 * 🔑 THE VIEWER IS COMPARED TO THE APPROVER BY user_id, NEVER BY DISPLAY NAME.
 * `users.display_name` has no uniqueness on either engine, so a name comparison
 * would hide the button from a homonymous colleague who is perfectly entitled to
 * poke, and — more to the point — is the name-used-as-identity defect class this
 * repo has shipped three fixes for. `approval_info.approver_user_id` is supplied
 * by the server for exactly this comparison (server/routes/orders.js, both
 * `approval_info` build sites).
 *
 * 🔑 A MISSING / NULL ID IS NOT A MATCH. `order_approval_steps.approver_user_id`
 * is left NULL on purpose when a workflow template's approver name matched two
 * people ("an ambiguous name binds NOBODY") and on legacy rows. NULL therefore
 * means "this step binds nobody", not "this step might be you", so the control
 * stays visible. That is the same choice the server gate makes, and the two must
 * not diverge: refuse only on a positive id match.
 *
 * ⚠️ AND THIS IS WHY IT DIFFERS FROM `getNextStepInfo`'s "Your Approval Needed"
 * LABEL, WHICH FAILS THE OTHER WAY ON THE SAME INPUT. Both obey one rule — act
 * only on a positive id match, never on a name — but they are two controls whose
 * SAFE DEFAULTS POINT IN OPPOSITE DIRECTIONS: an unusable id leaves this Poke
 * control VISIBLE (a redundant nudge is harmless; hiding a legitimate one is
 * not), and leaves that label ABSENT (a wrongly-offered "Your Approval Needed"
 * is not harmless). **They must NOT be "made consistent" by copying either one
 * into the other.** The difference is deliberate, it is the whole design, and it
 * will look like an inconsistency to whoever notices it next.
 *
 * ⚠️ This is a CONVENIENCE, not a gate. The enforcement is
 * `POST /api/orders/:id/poke`, which refuses `code: 'poke_self'` before writing
 * any notification or audit row. Never rely on this function for correctness.
 *
 * 🔀 TWO STAGES SINCE 2026-10-11 (owner). Everything above is the APPROVAL
 * stage (`submitted`). An `approved` order that nobody has placed can be poked
 * too — the nudge goes to the department's buyer — and for that stage this
 * returns `true` UNCONDITIONALLY: who would be poked is decided on the server
 * (the buyers, else the supervisors, minus the poker) and no list row carries
 * it, so the client cannot know whether the viewer is the only one who could
 * place the order. The server refuses that case (`poke_self`) and the refusal
 * is shown. Do NOT add a per-order lookup to a list endpoint to hide the
 * control — those endpoints are budgeted at zero extra queries per row.
 * The words for each stage are `pokeLabel` / `pokeTitle` below. The approved
 * stage is OFFERED on My Orders only (owner, 2026-10-11: "for now we can just
 * leave it to the individual who placed the order to poke") - Department
 * Orders adds its own `submitted` test at the call site; do not narrow it here.
 *
 * @param {object} order            – order row, incl. status + approval_info
 * @param {number|string} viewerUserId – the current user's id (localStorage.sfx_user_id is a string)
 * @returns {boolean}
 */
export function canPokeOrder(order, viewerUserId) {
  if (!order) return false;
  if (order.status === 'approved') return true;
  if (order.status !== 'submitted') return false;

  const info = order.approval_info;
  if (!info || !info.approver_name) return false;

  const approverId = normaliseUserId(info.approver_user_id);
  const viewerId = normaliseUserId(viewerUserId);

  // Refuse ONLY on a positive id match. Either id being unusable leaves the
  // control in place — a redundant self-notification is harmless, hiding a
  // legitimate poke of a colleague is not.
  if (approverId !== null && viewerId !== null && approverId === viewerId) return false;

  return true;
}

/**
 * Who a poke on this order reaches, as the control's words. ONE definition for
 * every Poke control, so a menu item and a button cannot name different people.
 *
 * `submitted` → the current approver; `approved` → the buyer (the order is
 * waiting to be placed). Any other status is not pokeable (`canPokeOrder`), and
 * falls to the approver wording only so a caller never renders `undefined`.
 *
 * `pokeLabel(order)`            – the full label: "Poke approver" / "Poke buyer".
 * `pokeLabel(order, { short })` – for a button that already sits beside the
 *   "Next step" text naming the approver: "Poke" at the approval stage, as it
 *   has always read; the ordering stage keeps "Poke buyer", because nothing
 *   beside the button says who that is.
 * `pokeTitle(order)`            – the tooltip. A poke is an in-app notification;
 *   it sends no email, so neither sentence claims one.
 */
export function pokeLabel(order, { short = false } = {}) {
  if (order && order.status === 'approved') return 'Poke buyer';
  return short ? 'Poke' : 'Poke approver';
}

export function pokeTitle(order) {
  if (order && order.status === 'approved') return 'Send the buyer a reminder to place this order';
  return 'Send the current approver a reminder';
}

/**
 * Is the step's bound approver a DIFFERENT person from the viewer?
 *
 * The predicate behind the 2026-08-18 "approve on behalf" confirmation
 * (`components/ApproveOnBehalfDialog.js`) — the owner's ruling from the Jake
 * Dimmer incident: a manager holding `canManageDepartmentOrders` may act on
 * ANY step in the department, not only their own, and the app never said so.
 *
 * 🔑 SAME COERCION AS `canPokeOrder` AND `getNextStepInfo`'s "Your Approval
 * Needed" label — one `normaliseUserId` in this file, never a second one.
 *
 * ⚠️ THE SAFE DEFAULT ON AN UNUSABLE ID FOLLOWS THE LABEL, NOT THE POKE
 * BUTTON, and for the same reason the label differs from Poke: a NULL
 * `approver_user_id` means "an ambiguous name binds NOBODY" — there is no
 * true claim this dialog could make about whose step it is, so it says
 * nothing (`false`, matching the label's absence) rather than firing a
 * warning that names no one. An unusable VIEWER id against a real, bound
 * approver is the opposite case: there IS a specific person the step
 * belongs to and we cannot prove it is this viewer, so this returns `true`
 * — the failure direction Poke also takes, because a redundant explanation
 * is harmless and a silently skipped one is not.
 *
 * @param {number|string|null|undefined} approverUserId
 * @param {number|string|null|undefined} viewerUserId
 * @returns {boolean}
 */
export function isDifferentApprover(approverUserId, viewerUserId) {
  const approverId = normaliseUserId(approverUserId);
  if (approverId === null) return false;
  const viewerId = normaliseUserId(viewerUserId);
  return viewerId === null || approverId !== viewerId;
}

/**
 * The order-list-row-shaped wrapper around {@link isDifferentApprover}:
 * "is THIS order's current step, as it reads on a list row, someone else's?"
 *
 * Mirrors `canPokeOrder`'s guard clauses (`order.status === 'submitted'`,
 * `order.approval_info` present) so the two predicates agree about which
 * rows are even in play, before either asks whose step it is.
 *
 * @param {object} order — order row (status + approval_info)
 * @param {number|string} viewerUserId
 * @returns {boolean}
 */
export function isApprovingSomeoneElsesStep(order, viewerUserId) {
  if (!order || order.status !== 'submitted') return false;
  const info = order.approval_info;
  if (!info) return false;
  return isDifferentApprover(info.approver_user_id, viewerUserId);
}

export function getNextStepInfo(order, opts = {}) {
  const { canAction = false, role = 'user', userId } = opts;
  const isUser = role === 'user';
  const isAdmin = role === 'admin';
  // Dept roles: buyer or sub_department_lead
  const isDept = role === 'buyer' || role === 'sub_department_lead';

  switch (order.status) {
    // ----- SUBMITTED -----
    case 'submitted': {
      if (order.approval_info && order.approval_info.overall_status === 'pending') {
        const stepSub = `Step ${order.approval_info.current_step}/${order.approval_info.total_steps}`;

        if (isUser) {
          // User view: show who needs to approve, with role label.
          // 🔀 2026-09-11: the expression moved to
          // `constants/statuses.js#approverRoleLabel`, SHARED with
          // `constants/rowStyles.js#chevronTitle` so the step panel and the
          // chevron tooltip can never disagree about what a role is called.
          // Byte-identical behaviour — same map, same `|| role || 'Approver'`.
          const roleLabel = approverRoleLabel(order.approval_info.approver_role);
          return {
            label: `Waiting for approval from ${order.approval_info.approver_name} ${roleLabel}`,
            sublabel: stepSub,
            action: null,
            color: 'text-blue-600',
            icon: '⏳',
          };
        }

        if (isDept) {
          const approverName = order.approval_info.approver_name || '';
          // 🔴 DELIBERATELY **NOT** `approverRoleLabel` — its fallback is
          // `'Approver'` and this branch needs `''`. Here the label is appended
          // to a name inside a longer sentence, where a literal "Approver" for a
          // role the map does not know reads wrongly. Changing it would alter a
          // shipped sentence; see that helper's docblock.
          const roleLabel = APPROVAL_ROLE_LABELS[order.approval_info.approver_role] || order.approval_info.approver_role || '';

          // 🔑 "Your Approval Needed" IS DECIDED BY user_id, NEVER BY DISPLAY NAME.
          // This was `userName && approverName === userName` until 2026-08-06.
          // `users.display_name` has no uniqueness guarantee on either engine
          // (plain `TEXT NOT NULL` + a NON-unique `idx_users_display_name`), so two
          // colleagues sharing a name both saw the label — or, after a rename, the
          // real approver stopped seeing it. `approval_info.approver_user_id` is on
          // the wire from both server build sites (server/routes/orders.js, the
          // `GET /department` and `GET /` blocks) precisely so this comparison can
          // be made by identity.
          //
          // 🔑 BOTH SIDES ARE COERCED FIRST, via the SAME `normaliseUserId` that
          // `canPokeOrder` uses — one coercion in this file, never two.
          // `localStorage.sfx_user_id` is a STRING and the API sends a NUMBER, so a
          // bare `===` between them is ALWAYS FALSE. That failure is invisible on
          // inspection: the code reads as an id comparison and simply never matches,
          // which here would mean the label silently never appears for anyone.
          //
          // 🔑 A NULL `approver_user_id` IS NOT A MATCH, AND THERE IS NO FALLBACK TO
          // THE NAME. It is deliberately NULL when a workflow template's approver
          // name matched two people ("an ambiguous name binds NOBODY") and on legacy
          // rows, so NULL means "this step binds nobody" — not "it might be you".
          // Falling back to `approverName === userName` on NULL would reinstate the
          // exact bug for exactly the ambiguous case the NULL exists to express: the
          // two homonyms whose collision caused the NULL are the two people the name
          // comparison would then match. The label is therefore ABSENT, and the row
          // falls through to the "<approver> to approve" branch below, which is what
          // a viewer who is not the bound approver should see.
          //
          // ⚠️ This deliberately differs from `canPokeOrder`, which leaves its
          // control VISIBLE on an unusable id. Same rule — refuse to act on anything
          // but a positive id match — pointed at two controls whose safe defaults are
          // opposites: a redundant poke is harmless, a wrongly-offered "Your Approval
          // Needed" is not. Do not "make them consistent" by copying one into the other.
          const approverId = normaliseUserId(order.approval_info.approver_user_id);
          const viewerId = normaliseUserId(userId !== undefined ? userId : readStoredViewerUserId());
          const isCurrentApprover = approverId !== null && viewerId !== null && approverId === viewerId;

          if (isCurrentApprover && canAction) {
            return {
              label: 'Your Approval Needed',
              sublabel: stepSub,
              action: 'approve',
              color: 'text-green-700 bg-green-50 hover:bg-green-100 border-green-200',
              icon: '!',
            };
          }

          // Not the current approver — show who is (buyer can still override)
          const approverDisplay = approverName || 'Approver';
          return {
            label: `${approverDisplay} ${roleLabel} to approve`,
            sublabel: stepSub,
            action: canAction ? 'approve' : null,
            color: canAction
              ? 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200'
              : 'text-blue-700 bg-blue-50 border-blue-200',
            icon: canAction ? '⏳' : '',
          };
        }

        // Admin view
        const approverDisplay = order.approval_info.approver_name || order.approval_info.approver_role || 'Approver';
        return {
          label: `${approverDisplay} to approve`,
          sublabel: stepSub,
          action: 'approve',
          color: 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200',
          icon: '⏳',
        };
      }

      // No approval_info
      if (isUser) {
        if (!order.department_id) {
          // Unassigned user — no approval workflow, order is just stored
          return { label: 'Order submitted', sublabel: null, action: null, color: 'text-green-600', icon: '✓' };
        }
        return { label: 'Waiting for approval', sublabel: null, action: null, color: 'text-blue-600', icon: '⏳' };
      }
      if (isDept) {
        return { label: 'Awaiting Approval', sublabel: 'Buyer', action: null, color: 'text-blue-700 bg-blue-50 border-blue-200', icon: '' };
      }
      // Admin
      return { label: 'Awaiting Approval', sublabel: 'Buyer', action: 'approve', color: 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200', icon: '⏳' };
    }

    // ----- APPROVED -----
    case 'approved':
      if (isUser) {
        return { label: 'Waiting to be ordered', sublabel: null, action: null, color: 'text-amber-600', icon: '📦' };
      }
      if (isDept) {
        return { label: 'Approved', sublabel: 'Ready to Order', action: null, color: 'text-green-700 bg-green-50 border-green-200', icon: '' };
      }
      return { label: 'Ready to Order', sublabel: 'Buyer', action: 'ordered', color: 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200', icon: '📦' };

    // ----- REJECTED -----
    case 'rejected':
      if (isUser) {
        return { label: 'Needs revision and resubmission', sublabel: null, action: null, color: 'text-red-600', icon: '✗' };
      }
      return { label: 'Rejected', sublabel: `${order.requested_by || 'User'} to resubmit`, action: null, color: 'text-red-700 bg-red-50 border-red-200', icon: isDept ? '' : '✗' };

    // ----- ORDERED -----
    case 'ordered': {
      const info = order.delivery_info;

      if (isUser) {
        if (!order.department_id) {
          // Unassigned user — simplified lifecycle
          return { label: 'Ordered — awaiting delivery', sublabel: null, action: null, color: 'text-amber-600', icon: '📦' };
        }
        if (info && info.received_items > 0) {
          return { label: 'Waiting for delivery', sublabel: `${info.received_items}/${info.total_items} items received`, action: null, color: 'text-orange-600', icon: '🚚' };
        }
        return { label: 'Waiting for delivery', sublabel: null, action: null, color: 'text-orange-600', icon: '🚚' };
      }

      if (isDept) {
        return {
          label: 'Ordered',
          sublabel: info && info.received_items > 0
            ? `${info.received_items}/${info.total_items} received`
            : 'Awaiting Delivery',
          action: null,
          color: 'text-purple-700 bg-purple-50 border-purple-200',
          icon: '',
        };
      }

      // Admin - includes supplier hint and action
      const supplierHint = order.supplier_name ? `from ${order.supplier_name}` : null;
      if (info && info.received_items > 0) {
        return {
          label: 'Awaiting Delivery',
          sublabel: `${info.received_items}/${info.total_items} received${supplierHint ? ` ${supplierHint}` : ''}`,
          action: 'delivered',
          color: 'text-orange-700 bg-orange-50 hover:bg-orange-100 border-orange-200',
          icon: '🚚',
        };
      }
      return {
        label: 'Awaiting Delivery',
        sublabel: supplierHint,
        action: 'delivered',
        color: 'text-orange-700 bg-orange-50 hover:bg-orange-100 border-orange-200',
        icon: '🚚',
      };
    }

    // ----- PARTIALLY DELIVERED -----
    case 'partially_delivered': {
      const info = order.delivery_info;

      if (isUser) {
        return {
          label: 'Waiting for remaining items',
          sublabel: info ? `${info.received_items}/${info.total_items} items received` : null,
          action: null,
          color: 'text-orange-600',
          icon: '📬',
        };
      }

      if (isDept) {
        return {
          // 🔻 'Partially Delivered' → 'Partial delivery' (owner, 2026-09-13).
          // A hand-written copy of `STATUS_LABELS.partially_delivered`; the
          // `isUser` branch above deliberately says something else entirely
          // ("Waiting for remaining items") and is NOT a copy, so it stands.
          label: 'Partial delivery',
          sublabel: info ? `${info.received_items}/${info.total_items} received` : null,
          action: null,
          color: 'text-orange-700 bg-orange-50 border-orange-200',
          icon: '',
        };
      }

      // Admin - includes supplier hint and action
      const supplierHint = order.supplier_name ? `from ${order.supplier_name}` : null;
      return {
        label: 'Awaiting Remaining',
        sublabel: info ? `${info.received_items}/${info.total_items} received${supplierHint ? ` ${supplierHint}` : ''}` : supplierHint,
        action: 'delivered',
        color: 'text-orange-700 bg-orange-50 hover:bg-orange-100 border-orange-200',
        icon: '📬',
      };
    }

    // ----- DELIVERED -----
    case 'delivered':
      if (isUser) {
        return { label: 'Complete', sublabel: null, action: null, color: 'text-teal-600', icon: '✓' };
      }
      return { label: 'Complete', sublabel: null, action: null, color: 'text-teal-700 bg-teal-50 border-teal-200', icon: isDept ? '' : '✓' };

    // ----- CANCELLED -----
    case 'cancelled':
      if (isUser) {
        return { label: '', sublabel: null, action: null, color: 'text-gray-600 dark:text-gray-400', icon: '' };
      }
      return { label: 'Cancelled', sublabel: null, action: null, color: 'text-gray-500 bg-gray-50 border-gray-200', icon: '' };

    // ----- DEFAULT -----
    default:
      if (isUser) {
        return { label: '', sublabel: null, action: null, color: 'text-gray-600 dark:text-gray-400', icon: '' };
      }
      return { label: '-', sublabel: null, action: null, color: 'text-gray-600 dark:text-gray-400', icon: '' };
  }
}
